import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { EmptyState } from '../components/ui/EmptyState';
import { Icon } from '../components/ui/Icon';
import { PageHeader } from '../components/ui/PageHeader';
import { StatusBadge, type StatusVariant } from '../components/ui/StatusBadge';
import {
  createBetaInvitation, listBetaInvitations, PlatformApiError, resendBetaInvitation, revokeBetaInvitation,
  type BetaInvitation, type BetaInvitationList, type InvitationState,
} from './api';
import { navigatePlatform } from './PlatformApp';
import { COMMERCIAL_LABEL, funnelRows, lastErrorLabel, medianRows, STEP_LABEL } from './betaFunnel';
import { ReasonDialog } from './ReasonDialog';

// ONB-010 (Macrofase C1): invitaciones a la BETA PRIVADA. Plataforma invita UN correo; quien acepta crea su cuenta y
// su negocio con el registro guiado de siempre, gratis (plan Piloto, sin trial ni cobro). Aquí se crea, se ve el
// estado, se reenvía (rota el enlace) y se revoca (con motivo). El servidor decide todo; esta pantalla solo presenta.
// El enlace de invitación nunca llega aquí: viaja solo en el correo.

const STATE: Record<InvitationState, { label: string; variant: StatusVariant; detail: string }> = {
  PENDING: { label: 'Pendiente', variant: 'info', detail: 'Enviada; todavía no la acepta.' },
  EXPIRED: { label: 'Vencida', variant: 'warning', detail: 'El enlace venció sin usarse. Reenvíala para darle 7 días más.' },
  ACCEPTED: { label: 'Cuenta creada', variant: 'info', detail: 'Ya creó su cuenta; falta que prepare su negocio. Si no fue esa persona, revócala (reenviar no basta).' },
  ACCEPTED_EXPIRED: { label: 'Sin terminar', variant: 'warning', detail: 'Creó su cuenta pero no preparó su negocio a tiempo. Reenviarla la deja empezar de nuevo.' },
  PROVISIONED: { label: 'Negocio creado', variant: 'success', detail: 'Ya usa Negocio360.' },
  REVOKED: { label: 'Revocada', variant: 'neutral', detail: 'El enlace ya no sirve.' },
};
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const fmtDate = (iso: string | null) => (iso ? new Date(iso).toLocaleString('es-MX', { dateStyle: 'medium', timeStyle: 'short' }) : '—');
const errorText = (error: unknown, fallback: string) => (error instanceof PlatformApiError ? error.message : fallback);

/** P-085: dónde va el tester, cuándo avanzó, su último error y su condición comercial. Lo decide el servidor. */
function JourneyCell({ inv }: { inv: BetaInvitation }) {
  const journey = inv.journey;
  if (!journey) return <small>—</small>;
  const error = lastErrorLabel(journey.lastError);
  return (
    <span className="platform-journey">
      <strong>{STEP_LABEL[journey.step]}</strong>
      <small>Último avance: {fmtDate(journey.lastProgressAt)}</small>
      {journey.lastActivityAt && journey.lastActivityAt !== journey.lastProgressAt && <small>Última actividad: {fmtDate(journey.lastActivityAt)}</small>}
      {journey.resumeCount > 0 && <small>Volvió {journey.resumeCount === 1 ? '1 vez' : `${journey.resumeCount} veces`}</small>}
      {error && <small className="platform-journey__error">{error}</small>}
      {inv.commercial && <small>{COMMERCIAL_LABEL[inv.commercial.mode]}</small>}
    </span>
  );
}

export function PlatformInvitationsPage() {
  const [data, setData] = useState<BetaInvitationList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>();
  const [notice, setNotice] = useState<string>();
  const [email, setEmail] = useState('');
  const [note, setNote] = useState('');
  const [tried, setTried] = useState(false);
  const [creating, setCreating] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [revoking, setRevoking] = useState<BetaInvitation | null>(null);
  // Ese correo ya entra a otro negocio: el servidor pide confirmación expresa antes de invitarlo.
  const [existingFor, setExistingFor] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try { setData(await listBetaInvitations()); setError(undefined); }
    catch (err) { setError(errorText(err, 'No fue posible cargar las invitaciones.')); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void load(); }, [load]);

  // P-085: con la beta cerrada a testers nuevos no se invita (el servidor lo niega igual); reenviar para SEGUIR sí.
  const canSend = !!data?.enabled && !!data?.emailDeliveryConfigured && !data?.closedToNew;
  const canResendAny = !!data?.enabled && !!data?.emailDeliveryConfigured;
  const emailError = !EMAIL_PATTERN.test(email.trim()) ? 'Escribe un correo válido.' : undefined;

  const send = async (confirmExistingAccount: boolean) => {
    setCreating(true); setError(undefined); setNotice(undefined);
    try {
      const created = await createBetaInvitation(email.trim(), note.trim() || undefined, confirmExistingAccount);
      setNotice(`Invitación enviada a ${created.email}.`);
      setEmail(''); setNote(''); setTried(false); setExistingFor(null);
      await load();
    } catch (err) {
      if (err instanceof PlatformApiError && err.code === 'PRIVATE_BETA_EMAIL_HAS_ACCOUNT') { setExistingFor(email.trim()); return; }
      setError(errorText(err, 'No fue posible enviar la invitación.'));
    } finally { setCreating(false); }
  };

  const create = async (event: FormEvent) => {
    event.preventDefault();
    if (creating) return;
    setTried(true);
    if (emailError || !canSend) return;
    await send(false);
  };

  const resend = async (invitation: BetaInvitation) => {
    if (busyId) return;
    setBusyId(invitation.id); setError(undefined); setNotice(undefined);
    try {
      await resendBetaInvitation(invitation.id);
      setNotice(`Reenviada a ${invitation.email}. El enlace anterior ya no sirve.`);
      await load();
    } catch (err) { setError(errorText(err, 'No fue posible reenviar la invitación.')); }
    finally { setBusyId(null); }
  };

  const revoke = async (reason: string) => {
    if (!revoking) return;
    setBusyId(revoking.id); setError(undefined); setNotice(undefined);
    try {
      await revokeBetaInvitation(revoking.id, reason);
      setNotice(`Invitación de ${revoking.email} revocada.`);
      setRevoking(null);
      await load();
    } catch (err) { setError(errorText(err, 'No fue posible revocar la invitación.')); }
    finally { setBusyId(null); }
  };

  const actions = (inv: BetaInvitation) => (
    <span className="platform-actions">
      {inv.canResend && (
        <button className="text-button" disabled={!canResendAny || busyId !== null} onClick={() => void resend(inv)} type="button">
          {busyId === inv.id ? 'Enviando…' : 'Reenviar'}
        </button>
      )}
      {inv.canRevoke && <button className="text-button text-button--danger" disabled={busyId !== null} onClick={() => setRevoking(inv)} type="button">Revocar</button>}
      {inv.business && <button className="text-button" onClick={() => navigatePlatform(`#platform/businesses/${inv.business!.id}`)} type="button">Ver negocio <Icon name="chevron-right" size={14} /></button>}
    </span>
  );

  return (
    <section aria-label="Invitaciones a la beta privada">
      <PageHeader
        title="Invitaciones a la beta privada"
        description="Invita a un correo concreto. Quien acepta crea su negocio gratis durante la beta, con el plan Piloto, sin prueba de 15 días ni cobro."
        actions={<button className="button button--secondary" onClick={() => navigatePlatform('#platform')} type="button"><Icon name="chevron-left" size={16} /> Negocios</button>}
      />

      {data && !data.enabled && (
        <div className="alert alert--warning" role="status">Las invitaciones están apagadas en este servidor. Puedes ver y revocar las que existan; para invitar hace falta encenderlas con autorización.</div>
      )}
      {data && data.enabled && !data.emailDeliveryConfigured && (
        <div className="alert alert--warning" role="status">El correo de la plataforma no está configurado: una invitación no podría llegar.</div>
      )}
      {data && data.enabled && data.closedToNew && (
        <div className="alert alert--info" role="status">La beta está cerrada a testers nuevos: no se puede invitar ni reenviar una invitación pendiente. Las cuentas ya creadas siguen hasta su negocio, y a ellas sí se les puede reenviar el enlace para seguir.</div>
      )}
      {data?.summary && (
        <section aria-label="Embudo de la beta" className="platform-funnel">
          <p className="platform-funnel__scope">Embudo de las últimas {data.summary.listLimit} invitaciones, sin las revocadas.</p>
          <ul className="platform-funnel__steps">
            {funnelRows(data.summary).map((row) => (
              <li className="platform-funnel__step" key={row.label}><strong>{row.value}</strong><span>{row.label}</span></li>
            ))}
          </ul>
          <ul className="platform-funnel__times">
            {medianRows(data.summary).map((row) => (
              <li key={row.label}><span>{row.label}</span> <strong>{row.value}</strong></li>
            ))}
          </ul>
        </section>
      )}
      {error && <div className="alert alert--error" role="alert">{error}</div>}
      {notice && <div className="alert alert--success" role="status">{notice}</div>}
      {existingFor && existingFor === email.trim() && (
        <div className="alert alert--warning" role="alert">
          <p>{existingFor} ya entra a otro negocio de Negocio360. Si lo invitas, al iniciar sesión tendrá que indicar a qué negocio entra.</p>
          <span className="platform-actions">
            <button className="button button--secondary" disabled={creating} onClick={() => void send(true)} type="button">{creating ? 'Enviando…' : 'Invitar de todos modos'}</button>
            <button className="text-button" disabled={creating} onClick={() => setExistingFor(null)} type="button">Cancelar</button>
          </span>
        </div>
      )}

      <form className="platform-filters" noValidate onSubmit={(event) => void create(event)}>
        <label className="field"><span>Correo de la persona *</span>
          <input
            aria-invalid={tried && !!emailError}
            autoComplete="off"
            inputMode="email"
            maxLength={100}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="nombre@correo.com"
            type="email"
            value={email}
          />
          {tried && emailError && <small className="platform-form__error">{emailError}</small>}
        </label>
        <label className="field"><span>Nota interna (opcional)</span>
          <input maxLength={200} onChange={(e) => setNote(e.target.value)} placeholder="Para quién es (no va en el correo)" value={note} />
        </label>
        <button className="button button--primary" disabled={creating || !canSend} type="submit">
          <Icon name="plus" size={16} /> {creating ? 'Enviando…' : 'Invitar'}
        </button>
      </form>

      {loading && !data ? <div className="inline-loading"><span className="spinner" />Cargando…</div> : !data || data.invitations.length === 0 ? (
        <EmptyState title="Sin invitaciones" description="Todavía no has invitado a nadie a la beta." icon="users" />
      ) : (
        <>
          <div className="data-table-wrap platform-table">
            <table className="data-table">
              <thead><tr><th>Correo</th><th>Estado</th><th>Recorrido</th><th>Envíos</th><th>Vence</th><th>Creada</th><th></th></tr></thead>
              <tbody>
                {data.invitations.map((inv) => (
                  <tr key={inv.id}>
                    <td><strong>{inv.email}</strong>{inv.note && <small>{inv.note}</small>}</td>
                    <td><StatusBadge variant={STATE[inv.state].variant}>{STATE[inv.state].label}</StatusBadge><small>{inv.business?.name ?? STATE[inv.state].detail}</small></td>
                    <td><JourneyCell inv={inv} /></td>
                    <td>{inv.sendCount}<small>{fmtDate(inv.lastSentAt)}</small></td>
                    <td>{inv.state === 'PENDING' || inv.state === 'ACCEPTED' ? fmtDate(inv.expiresAt) : '—'}</td>
                    <td>{fmtDate(inv.createdAt)}<small>{inv.invitedBy ?? ''}</small></td>
                    <td>{actions(inv)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <ul className="platform-cards-list">
            {data.invitations.map((inv) => (
              <li key={inv.id}>
                <div className="platform-biz-card platform-biz-card--static">
                  <div className="platform-biz-card__head"><strong>{inv.email}</strong><StatusBadge variant={STATE[inv.state].variant}>{STATE[inv.state].label}</StatusBadge></div>
                  <span>{inv.business?.name ?? STATE[inv.state].detail}</span>
                  <span>Envíos {inv.sendCount} · Creada {fmtDate(inv.createdAt)}</span>
                  <JourneyCell inv={inv} />
                  {actions(inv)}
                </div>
              </li>
            ))}
          </ul>
        </>
      )}

      {revoking && (
        <ReasonDialog
          busy={busyId === revoking.id}
          confirmLabel="Revocar invitación"
          danger
          description={`El enlace enviado a ${revoking.email} dejará de servir. Si ya creó su cuenta, no podrá preparar su negocio.`}
          onCancel={() => setRevoking(null)}
          onConfirm={(reason) => void revoke(reason)}
          title="Revocar invitación"
        />
      )}
    </section>
  );
}
